import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { changePassword } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ArrowRight, Info } from "@phosphor-icons/react";
import { ContourBackground } from "@/components/marketing/SresVisuals";
import PasskeySettings from "@/components/PasskeySettings";
import useSEO from "@/lib/useSEO";

/**
 * /change-password — mandatory first-login screen + self-service rotation +
 * passkey / biometric enrolment. Same brand grammar as /login and /signup.
 */
export default function ChangePasswordPage() {
  useSEO({
    title: "Security",
    description: "Rotate your SRES workspace password and manage biometric sign-in.",
    path: "/change-password",
  });

  const nav = useNavigate();
  const [sp] = useSearchParams();
  const forced = sp.get("first") === "1";
  const { refresh } = useAuth();
  const [cur, setCur] = useState("");
  const [nx, setNx] = useState("");
  const [nx2, setNx2] = useState("");
  const [pending, setPending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (nx !== nx2) { toast.error("New passwords do not match"); return; }
    if (nx.length < 8) { toast.error("Min 8 characters"); return; }
    setPending(true);
    try {
      await changePassword({ current_password: cur, new_password: nx });
      toast.success("Password updated");
      await refresh();
      nav("/app", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to change password");
    } finally { setPending(false); }
  };

  return (
    <div
      className="min-h-screen w-full px-5 sm:px-6 py-10 sm:py-14 relative overflow-x-hidden bg-white"
      style={{
        background:
          "radial-gradient(900px 600px at 50% -10%, rgba(30,111,255,0.08), transparent 60%), #FFFFFF",
      }}
      data-testid="change-password-page"
    >
      <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none" aria-hidden />
      <div
        className="absolute left-1/2 -translate-x-1/2 -top-24 w-[720px] max-w-none h-[560px] opacity-[0.55] pointer-events-none"
        aria-hidden
      >
        <ContourBackground />
      </div>

      <div className="relative w-full max-w-[560px] mx-auto flex flex-col gap-6">
        {/* Brand block */}
        <div className="text-center pt-2">
          <div className="flex justify-center">
            <img
              src="/sres-logo-horizontal.png?v=5"
              alt="SRES · Shanvi Resource Estimation Studio"
              className="h-12 sm:h-14 w-auto object-contain max-w-full"
              draggable={false}
            />
          </div>
          <div className="mt-5 inline-flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">
            <span className="w-6 h-px bg-brand-blue" />
            Account security
          </div>
          <h1
            className="mt-4 font-heading font-black text-slate-900 text-3xl sm:text-4xl tracking-[-0.02em]"
            data-testid="cp-heading"
          >
            Manage sign-in
          </h1>
        </div>

        {/* Password card */}
        <form
          onSubmit={submit}
          className="bg-white border border-slate-200 rounded-2xl shadow-[0_20px_60px_-30px_rgba(15,23,42,0.18)] p-5 sm:p-8"
          data-testid="change-password-form"
        >
          <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500 mb-4">
            Password
          </div>

          {forced && (
            <div
              className="mb-5 flex items-start gap-2.5 rounded-lg border border-brand-blue/30 bg-brand-blue/5 px-3.5 py-3"
              data-testid="cp-forced-banner"
            >
              <Info size={16} weight="bold" className="text-brand-blue shrink-0 mt-0.5" />
              <div className="text-[13.5px] leading-snug text-slate-700">
                First-time sign-in — please pick a new password before continuing.
              </div>
            </div>
          )}

          <div className="space-y-4 text-left">
            <Fld label="Current password" v={cur} on={setCur} tid="cp-current" />
            <Fld label="New password" v={nx} on={setNx} tid="cp-new" />
            <Fld label="Confirm new password" v={nx2} on={setNx2} tid="cp-confirm" />

            <Button
              type="submit"
              disabled={pending}
              className="w-full h-12 bg-slate-900 hover:bg-black text-white font-semibold rounded-md text-[15px] mt-2 inline-flex items-center justify-center gap-2"
              data-testid="cp-submit"
            >
              {pending ? "Updating…" : (<>Update password <ArrowRight size={16} weight="bold" /></>)}
            </Button>
          </div>

          <div className="mt-5 text-center text-[12px] text-slate-500">
            Minimum 8 characters. Choose something unique and store it in a password manager.
          </div>
        </form>

        {/* Passkey / biometric management */}
        {!forced && <PasskeySettings />}
      </div>
    </div>
  );
}

function Fld({ label, v, on, tid }) {
  return (
    <div>
      <Label className="text-[13px] font-semibold text-slate-800">{label}</Label>
      <Input
        type="password"
        required
        value={v}
        onChange={(e) => on(e.target.value)}
        autoComplete={tid === "cp-current" ? "current-password" : "new-password"}
        className="mt-2 h-12 bg-slate-50 border-slate-200 rounded-md text-slate-900 focus-visible:ring-brand-blue focus-visible:border-brand-blue focus-visible:bg-white text-[16px]"
        data-testid={tid}
      />
    </div>
  );
}
